import { t } from "@lingui/macro";
import {
  ArrowCounterClockwiseIcon,
  CaretDownIcon,
  CheckIcon,
  SlidersHorizontalIcon,
  XIcon,
} from "@phosphor-icons/react";
import {
  createTemplatePalette,
  getOriginalTemplateColors,
  getTemplateColors,
  paletteFamilies,
  resolveTemplateColors,
} from "@reactive-resume/schema";
import { Popover, PopoverContent, PopoverTrigger } from "@reactive-resume/ui";
import { cn } from "@reactive-resume/utils";
import { useCallback, useId, useState } from "react";
import { createPortal } from "react-dom";

import { useResumeStore } from "@/client/stores/resume";

import { getColorLabels, getPaletteLabels } from "./color-labels";
import { FontControls } from "./font-controls";

const Swatches = ({ colors, large = false }: { colors: string[]; large?: boolean }) => (
  <span
    aria-hidden
    className={cn(
      "flex overflow-hidden rounded-md ring-1 ring-inset ring-black/10",
      large ? "h-7 w-full" : "h-5 w-12",
    )}
  >
    {colors.map((color, index) => (
      <span key={index} className="min-w-0 flex-1" style={{ backgroundColor: color }} />
    ))}
  </span>
);

const ColorField = ({
  label,
  color,
  onChange,
}: {
  label: string;
  color: string;
  onChange: (color: string) => void;
}) => {
  const id = useId();
  const [draft, setDraft] = useState<string | null>(null);
  return (
    <div className="min-w-0">
      <label
        htmlFor={id}
        className="mb-1.5 block truncate text-[11px] font-medium text-slate-600"
        title={label}
      >
        {label}
      </label>
      <div className="flex h-10 items-center gap-2 rounded-lg border border-slate-200 bg-white px-2 focus-within:ring-2 focus-within:ring-indigo-500">
        <input
          type="color"
          aria-label={t`Choisir : ${label}`}
          value={color}
          onChange={(event) => {
            setDraft(null);
            onChange(event.target.value);
          }}
          className="size-6 shrink-0 cursor-pointer rounded border-0 bg-transparent p-0 [&::-webkit-color-swatch-wrapper]:p-0 [&::-webkit-color-swatch]:rounded [&::-webkit-color-swatch]:border-0"
        />
        <input
          id={id}
          value={draft ?? color.toUpperCase()}
          spellCheck={false}
          maxLength={7}
          onChange={(event) => {
            const value = event.target.value;
            setDraft(value);
            if (/^#[\da-f]{6}$/i.test(value)) onChange(value.toLowerCase());
          }}
          onBlur={() => setDraft(null)}
          onKeyDown={(event) => {
            if (event.key === "Enter") event.currentTarget.blur();
          }}
          className="min-w-0 flex-1 rounded border-0 bg-transparent p-0 font-mono text-[11px] text-slate-700 outline-none"
        />
      </div>
    </div>
  );
};

export const PalettePicker = ({ includeTypography }: { includeTypography: boolean }) => {
  const [open, setOpen] = useState(false);
  const [container, setContainer] = useState<HTMLElement | null>(null);
  const bindTrigger = useCallback((node: HTMLButtonElement | null) => {
    if (node) setContainer(node.closest<HTMLElement>('[role="dialog"]') ?? document.body);
  }, []);
  // Aperçu latéral : le panneau s’ouvre sur le formulaire pour laisser le CV visible.
  const besidePreview = container === document.body;
  const metadata = useResumeStore((state) => state.resume.data.metadata);
  const locked = useResumeStore((state) => state.resume.locked);
  const setValue = useResumeStore((state) => state.setValue);
  const slots = getTemplateColors(metadata.template);
  const current = resolveTemplateColors(metadata.template, metadata.theme);
  const labels = getColorLabels();
  const names = getPaletteLabels();
  const paletteSlots = slots.filter(
    ({ role }) => !["paper", "text", "lightDetails"].includes(role),
  );
  const previewSlots = (
    paletteSlots.length ? paletteSlots : slots.filter(({ key }) => key !== "paper")
  ).slice(0, 5);
  const palettes = [
    { id: "original", name: t`Originale`, colors: getOriginalTemplateColors(metadata.template) },
    ...paletteFamilies.map(({ id }) => ({
      id,
      name: names[id],
      colors: createTemplatePalette(metadata.template, id),
    })),
  ];
  const apply = (colors: Record<string, string>) => {
    setValue("metadata.theme", {
      ...metadata.theme,
      ...(metadata.template === "blue-wave" && { primary: colors["1c4165"] }),
      palette: { template: metadata.template, colors },
    });
  };
  const occurrences: Record<string, number> = {};
  return (
    <Popover modal open={open} onOpenChange={setOpen}>
      {open &&
        container &&
        createPortal(
          <div
            aria-hidden
            data-palette-backdrop
            className="fixed inset-0 z-50"
            style={{ pointerEvents: "auto" }}
            onPointerDown={() => setOpen(false)}
          />,
          container,
        )}
      <PopoverTrigger asChild>
        <button
          type="button"
          ref={bindTrigger}
          disabled={locked}
          className="flex h-10 shrink-0 items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-700 transition hover:border-indigo-300 hover:bg-indigo-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 disabled:opacity-50"
        >
          <Swatches colors={previewSlots.map(({ key }) => current[key])} />
          {includeTypography ? t`Personnaliser` : t`Couleurs`}
          <CaretDownIcon aria-hidden size={12} className="text-slate-400" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        portalContainer={container}
        aria-label={t`Le style de votre CV`}
        side={besidePreview ? "left" : "top"}
        align={besidePreview ? "end" : "start"}
        sideOffset={besidePreview ? 16 : undefined}
        collisionPadding={12}
        className="z-[60] w-[360px] max-w-[calc(100vw-24px)] overflow-y-auto overscroll-contain rounded-2xl border-slate-200 bg-white p-4 text-slate-900 shadow-xl"
        style={{ maxHeight: "min(75dvh, var(--radix-popover-content-available-height))" }}
      >
        <div className="mb-4 flex items-center justify-between gap-2">
          <h2 className="flex items-center gap-2 text-sm font-semibold text-slate-900">
            <SlidersHorizontalIcon aria-hidden size={18} className="text-indigo-600" />
            {t`Le style de votre CV`}
          </h2>
          <div className="flex items-center gap-1">
            <span className="rounded-full bg-indigo-50 px-2 py-1 text-[10px] font-medium capitalize text-indigo-700">
              {metadata.template.replace(/-/g, " ")}
            </span>
            <button
              type="button"
              aria-label={t`Fermer les réglages`}
              onClick={() => setOpen(false)}
              className="grid size-8 shrink-0 place-items-center rounded-lg text-slate-500 hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
            >
              <XIcon aria-hidden size={16} />
            </button>
          </div>
        </div>
        {includeTypography && (
          <div className="mb-4 border-b border-slate-100 pb-4">
            <FontControls expanded />
            <p className="mt-2 text-[10px] leading-4 text-slate-500">{t`La police du texte change. Les polices des titres du modèle sont conservées.`}</p>
          </div>
        )}
        <h3 className="mb-2 text-xs font-semibold">{t`Palettes harmonisées`}</h3>
        <div className="grid grid-cols-3 gap-2">
          {palettes.map((palette) => {
            const active = slots.every(
              ({ key }) => current[key]?.toLowerCase() === palette.colors[key]?.toLowerCase(),
            );
            return (
              <button
                key={palette.id}
                type="button"
                aria-pressed={active}
                aria-label={t`Palette ${palette.name}`}
                onClick={() => apply(palette.colors)}
                className={cn(
                  "min-w-0 rounded-xl border p-2 text-left transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500",
                  active
                    ? "border-indigo-500 bg-indigo-50"
                    : "border-slate-200 hover:border-indigo-300",
                )}
              >
                <Swatches large colors={previewSlots.map(({ key }) => palette.colors[key])} />
                <span className="mt-1.5 flex items-center justify-between gap-1 text-[10px] font-medium">
                  {palette.name}
                  {active && <CheckIcon aria-hidden size={12} className="text-indigo-600" />}
                </span>
              </button>
            );
          })}
        </div>
        <div className="mb-3 mt-5 flex items-center justify-between">
          <h3 className="text-xs font-semibold">
            {t`Couleurs du modèle`}{" "}
            <span className="font-normal text-slate-400">({slots.length})</span>
          </h3>
          <button
            type="button"
            onClick={() => apply(getOriginalTemplateColors(metadata.template))}
            className="flex items-center gap-1 rounded px-1 py-2 text-[10px] font-medium text-indigo-600 hover:bg-indigo-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
          >
            <ArrowCounterClockwiseIcon aria-hidden size={13} />
            {t`Couleurs d’origine`}
          </button>
        </div>
        <div className="grid grid-cols-2 gap-x-3 gap-y-3" key={metadata.template}>
          {slots.map((slot) => {
            occurrences[slot.role] = (occurrences[slot.role] ?? 0) + 1;
            const count = slots.filter(({ role }) => role === slot.role).length;
            const label = `${labels[slot.role]}${count > 1 ? ` ${occurrences[slot.role]}` : ""}`;
            return (
              <ColorField
                key={slot.key}
                label={label}
                color={current[slot.key]}
                onChange={(color) => apply({ ...current, [slot.key]: color })}
              />
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
};
